import { useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { FiPlus } from "react-icons/fi";
import { BsShop } from "react-icons/bs";
import { FaArrowRight } from "react-icons/fa6";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { createOrder, createBulkOrder, updateOrder } from "../../app/slices";

import { Button } from "@/components";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet.jsx";

import DeliveryDetails from "./DeliveryDetails";
import PickupAddress from "./PickupAddress";
import OrderDetails from "./OrderDetails";
import ProductDetails from "./ProductDetails";
import AddedProducts from "./AddedProducts";
import OtherDetails from "./OtherDetails";
import BulkOrder from "./BulkOrder";
import { SpaceWrapper } from "../../components";
import { useValidation, useFormData, useErrorPopup, useAlert } from "@/";
import { CreateOrderSchema } from "../../schemas";
import { BiEdit } from "react-icons/bi";

// Action Buttons Component
const ActionButtons = ({ onBulkSubmit, showSheet, setShowSheet }) => {
  return (
    <div className="w-full md:w-fit flex gap-2 justify-start lg:justify-end">
      <Sheet open={showSheet} onOpenChange={setShowSheet}>
        <SheetTrigger asChild>
          <Button variant="default" size="sm" icon={FiPlus}>
            Bulk Order
          </Button>
        </SheetTrigger>
        <SheetContent aria-describedby="">
          <SheetHeader>
            <SheetTitle>Add Bulk Order</SheetTitle>
            <BulkOrder onSubmit={onBulkSubmit} />
          </SheetHeader>
        </SheetContent>
      </Sheet>
      <Link to="/settings/channel">
        <Button variant="default" size="sm" icon={BsShop} className="h-12">
          Add Channel
        </Button>
      </Link>
    </div>
  );
};

const NewBooking = () => {
  const [showSheet, setShowSheet] = useState(false);
  const { orderData, isEdit, isView } = useLocation().state || {};
  const { alert, AlertDialog } = useAlert();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isDisabled, setIsDisabled] = useState(isView);
  const getInitialState = () => {
    const now = Date.now();
    return {
      orderId: `ORD${now}`,
      orderDate: new Date(),
      paymentMode: "",
      deliveryAddress: {
        lines: [{ line1: "", line2: "" }], //line1 is required, line2 is optional
        city: "",
        state: "",
        pincode: "",
        landmark: "",
        customerName: "",
        gstNumber: "", //optional
      },
      deliveryPhones: [
        { phoneNumber: "", phoneType: "Primary" },
        { phoneNumber: "", phoneType: "Secondary" }, //optional
      ],
      invoice: {
        invoiceDate: new Date(),
        invoiceNumber: `INV${now}`,
      },
      ebn: {
        hasEBN: false, //auto set to true if ebnNumber and ebnExpiryDate is provided
        ebnNumber: "", //required if sum of products.orderValue >= 50000
        ebnExpiryDate: "", //required if sum of products.orderValue >= 50000 and is of type date, my provide string so manage that
      },
      physicalWeight: undefined,
      dimensions: {
        length: undefined,
        breadth: undefined,
        height: undefined,
      },
      products: [],
    };
  };
  const [order, setOrder] = useState(orderData || getInitialState());
  const { validate, errors } = useValidation(CreateOrderSchema);
  const [fieldErrors, setFieldErrors] = useState([]);
  const apiLoading = useSelector((state) => state.order.loading) || false;
  console.log("API Loading:::", apiLoading);
  const convertToFromData = useFormData();
  const [bulkErrors, setBulkErrors] = useState([]);
  const { show, PopupComponent } = useErrorPopup(bulkErrors);
  const [product, setProduct] = useState({
    productName: "",
    quantity: undefined,
    orderValue: undefined,
    hsn: "",
    sku: "",
  });

  const addProduct = (prod) => {
    setOrder((prev) => ({
      ...prev,
      products: [...prev.products, prod],
    }));
  };

  const handleDelete = useCallback((index) => {
    setOrder((prev) => ({
      ...prev,
      products: prev.products.filter(
        (_, i) => prev.products.length - 1 - i !== index
      ),
    }));
  }, []);

  const manageNotAddedProduct = () => {
    console.log("Order Products:::", order.products);
    // if (order.products.length > 0) return;

    console.log("Product:::", product);
    if (
      product.productName &&
      product.quantity &&
      product.orderValue &&
      product.sku
    ) {
      console.log("There is a product details!!!");
      // Ensure quantity and values are positive
      if (product.quantity <= 0 || product.orderValue <= 0) {
        alert("Quantity and Order Value must be positive numbers.");
        return null;
      }
      console.log("Adding product!!!");

      addProduct(product);
      setProduct({
        productName: "",
        quantity: "",
        orderValue: "",
        hsn: "",
        sku: "",
      });
      console.log("Added product!!!");
      return product;
    }
    console.log("No proper product details to add!!!");
    return null;
  };

  const handleContinue = async () => {
    setFieldErrors([]);
    const addedProduct = manageNotAddedProduct();
    let currentOrder = { ...order };
    if (addedProduct) {
      currentOrder.products = [...currentOrder.products, addedProduct];
    }
    console.log("Order Data:::", currentOrder);
    const result = validate(currentOrder);
    console.log("Validation Result:::", result);
    if (!result.isValid) {
      console.log("Validation Error:::", result.errors);
      setFieldErrors(result.errors);
      return;
    }
    console.log("Validation Success:::", result.data);
    try {
      const action = await dispatch(createOrder(result.data));
      if (createOrder.fulfilled.match(action)) {
        setOrder(getInitialState());
        setProduct({
          productName: "",
          quantity: "",
          orderValue: "",
          hsn: "",
          sku: "",
        });
        alert("Order Created Successfully");
      } else if (createOrder.rejected.match(action)) {
        // Fallback for failed update
        console.error("Create Order failed:", action.payload);
        alert(`${action.payload || "Unknown error"}`);
      }
    } catch (error) {
      console.log("Order Creation Error:::", error);
    }
  };

  const handleUpdate = async () => {
    if (orderData.status === "Cancelled" || orderData.status === "Booked") {
      alert("Cancelled Orders cannot be updated");
      return;
    }

    setFieldErrors([]);

    const addedProduct = manageNotAddedProduct();
    let currentOrder = { ...order };
    if (addedProduct) {
      currentOrder.products = [...currentOrder.products, addedProduct];
    }

    console.log("Update Order Data:::", currentOrder);
    const result = validate(currentOrder);
    console.log("Update Validation Result:::", result);
    if (!result.isValid) {
      console.log("Update Validation Error:::", result.errors);
      setFieldErrors(result.errors);
      return;
    }
    console.log("Update Validation Success:::", result.data);
    try {
      const action = await dispatch(
        updateOrder({ orderData: result.data, id: order._id })
      );
      if (updateOrder.fulfilled.match(action)) {
        setOrder(getInitialState());
        setProduct({
          productName: "",
          quantity: "",
          orderValue: "",
          hsn: "",
          sku: "",
        });
        await alert("Order Updated Successfully");
        navigate("/bookedOrders");
      } else if (updateOrder.rejected.match(action)) {
        // Fallback for failed update
        console.error("Update failed:", action.payload);
        alert(`Failed to update order: ${action.payload || "Unknown error"}`);
      }
    } catch (error) {
      // Fallback for unexpected errors
      console.error("Unexpected error during update:", error);
      alert("An unexpected error occurred while updating the order");
    }
  };

  const BulkOrderSubmit = async (data) => {
    console.log("Bulk Order Data:::", data);
    const bulkOrderFormData = convertToFromData(data);
    console.log("Bulk Order FormData:::", bulkOrderFormData);

    try {
      const action = await dispatch(createBulkOrder(bulkOrderFormData));

      if (createBulkOrder.fulfilled.match(action)) {
        setShowSheet(false);
        alert("Bulk Orders Created Successfully");
        setBulkErrors([]); // Clear any previous errors
        return;
      }

      if (createBulkOrder.rejected.match(action)) {
        const errorDetails = action.payload?.details;
        const errorMessage =
          action.payload?.message || "Bulk Orders Creation Failed";

        if (!errorDetails) {
          console.log("Bulk Orders Creation Failed:", errorMessage);
          setShowSheet(false);
          alert(errorMessage);
          return;
        }

        setShowSheet(false);
        // Handle detailed errors
        setBulkErrors(errorDetails);
        show(errorDetails);
      }
    } catch (error) {
      console.error("Unexpected API Error:", error);
      alert("An unexpected error occurred while creating bulk orders");
    }
  };

  const handleEdit = () => {
    setIsDisabled(!isDisabled);
  };

  return (
    <SpaceWrapper>
      <div className="w-full flex-1 flex flex-col gap-4 p-4 pb-20 md:pb-4 relative max-w-[2000px]">
        <div className="flex flex-col md:flex-row gap-2 justify-between">
          <h2 className="text-headline3 md:text-headline2 text-secondary/95 font-nunito font-bold">
            {!orderData && "Create New Booking"}
            {isEdit && "Edit Order Details"}
            {isView && "View Order Details"}
          </h2>
          {!orderData && (
            <ActionButtons
              onBulkSubmit={BulkOrderSubmit}
              showSheet={showSheet}
              setShowSheet={setShowSheet}
            />
          )}
          {isView && orderData?.status !== "Booked" && (
            <Button
              variant="default"
              size="sm"
              icon={BiEdit}
              onClick={handleEdit}
            >
              {isDisabled ? "Edit" : "Cancel"}
            </Button>
          )}
        </div>

        <div className="flex flex-col md:flex-row gap-4 w-full">
          <DeliveryDetails
            data={order}
            setData={setOrder}
            errors={fieldErrors}
            isDisabled={isDisabled}
            isEdit={isEdit}
            isView={isView}
          />
          <div className="flex flex-col gap-4 w-full">
            <OrderDetails
              data={order}
              setData={setOrder}
              errors={fieldErrors}
              isDisabled={isDisabled}
              showShipment={isView || isEdit}
            />
          </div>
        </div>

        <ProductDetails
          onAddProduct={addProduct}
          product={product}
          setProduct={setProduct}
          errors={fieldErrors}
          isDisabled={isDisabled}
        />
        <AddedProducts
          products={order.products}
          handleDelete={handleDelete}
          setData={setOrder}
          errors={fieldErrors}
          isDisabled={isDisabled}
        />
        <OtherDetails
          data={order}
          setData={setOrder}
          errors={fieldErrors}
          isDisabled={isDisabled}
        />

        <div className="flex justify-end">
          {!isDisabled && (
            <Button
              variant="default"
              size="sm"
              icon={orderData?.status !== "Cancelled" && FaArrowRight}
              onClick={orderData ? handleUpdate : handleContinue}
              isLoading={apiLoading}
              loadingText={orderData ? "Updating..." : "Creating..."}
            >
              {orderData
                ? orderData.status === "Cancelled"
                  ? "Cancelled Order"
                  : "Update Order"
                : "Create Order"}
            </Button>
          )}
        </div>
      </div>
      <PopupComponent />
      <AlertDialog />
    </SpaceWrapper>
  );
};

export default NewBooking;
